import { I18N_NS } from "Config/i18n";
import { Tabs, TabsProps } from "antd";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { McpClientsTab } from "./McpClientsTab";
import { McpConnectionsTab } from "./McpConnectionsTab";
import { McpOverviewTab } from "./McpOverviewTab";
import { McpQuotaTab } from "./McpQuotaTab";
import { McpRegistrationsTab } from "./McpRegistrationsTab";

const TAB_KEYS = ["overview", "quota", "connections", "clients", "registrations"] as const;
type McpAdminTab = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<McpAdminTab, string> = {
  overview: "Overview",
  quota: "Quota",
  connections: "Connections",
  clients: "Clients",
  registrations: "Registrations",
};

const isTab = (value: string | undefined): value is McpAdminTab =>
  !!value && (TAB_KEYS as readonly string[]).includes(value);

const renderTab = (key: McpAdminTab): ReactNode => {
  switch (key) {
    case "overview":
      return <McpOverviewTab />;
    case "quota":
      return <McpQuotaTab />;
    case "connections":
      return <McpConnectionsTab />;
    case "clients":
      return <McpClientsTab />;
    case "registrations":
      return <McpRegistrationsTab />;
  }
};

export const McpAdmin = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const activeKey: McpAdminTab = isTab(tab) ? tab : "overview";

  useEffect(() => {
    if (tab && !isTab(tab)) {
      navigate({ pathname: "/admin/mcp/overview", search: location.search }, { replace: true });
    }
  }, [tab, navigate, location.search]);

  const items: TabsProps["items"] = TAB_KEYS.map((key) => ({
    key,
    label: t(TAB_LABELS[key]),
    children: renderTab(key),
  }));

  return (
    <div>
      <Tabs
        activeKey={activeKey}
        destroyOnHidden
        onChange={(key) => navigate({ pathname: `/admin/mcp/${key}`, search: location.search })}
        items={items}
      />
    </div>
  );
};
